<template>
  <div class="rk-home">
    <!-- Scroll progress indicator -->
    <div class="rk-progress" :style="{ '--scroll-progress': scrollProgress }"></div>

    <!-- Non-linear floating section navigation -->
    <SectionNav />

    <header-section></header-section>

    <section id="home">
      <hero-section></hero-section>
    </section>

    <stats-section></stats-section>

    <how-it-works-section></how-it-works-section>

    <features></features>

    <dashboard-preview-section></dashboard-preview-section>

    <property-types-section></property-types-section>

    <testimonials-section></testimonials-section>

    <security-section></security-section>

    <!-- <faq-section></faq-section> -->

    <!-- CTA -->
    <section class="rk-cta">
      <div class="rk-container rk-cta__inner" v-reveal="{ variant: 'scale' }">
        <span class="rk-eyebrow rk-eyebrow--light"><i class="bi bi-gear"></i> No forced payment gateway</span>
        <h2>Ready to run your properties from one dashboard?</h2>
        <p>We tailor Rental Kenya to how your business already collects and reconciles rent — join 500+ landlords who've made the switch.</p>
        <router-link to="/book-demo" class="rk-btn rk-btn--primary rk-btn--lg">
          <span>Book your demo today</span>
          <i class="bi bi-arrow-right"></i>
        </router-link>
      </div>
    </section>

    <section id="about">
      <footer-section></footer-section>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import SectionNav from '../layouts/SectionNav.vue'
import StatsSection from '../layouts/StatsSection.vue'
import HowItWorksSection from '../layouts/Howitworkssection.vue'
import DashboardPreviewSection from '../layouts/Dashboardpreviewsection.vue'
import PropertyTypesSection from '../layouts/Propertytypesection.vue'
import TestimonialsSection from '../layouts/Testimonialsection.vue'
import SecuritySection from '../layouts/Securitysection.vue'
//import FaqSection from '../layouts/Faqsection.vue'
import Features from '../layouts/Features.vue'

const scrollProgress = ref(0)
const onScroll = () => {
  const h = document.documentElement
  const scrollable = h.scrollHeight - h.clientHeight
  scrollProgress.value = scrollable > 0 ? h.scrollTop / scrollable : 0
}
onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))
</script>

<style scoped>
.rk-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
.rk-section { padding: 6rem 0; background: var(--paper); }

/* Features section head — left-aligned to match the new kicker */
.rk-section__head { max-width: 620px; margin: 0 0 3.5rem; text-align: left; }
.rk-section__head h2 {
  font-family: var(--font-display); font-size: 2.6rem; font-weight: 400;
  color: var(--ink); margin: 0.9rem 0 0.85rem; line-height: 1.15;
}
.rk-section__head p { font-family: var(--font-body); font-size: 1.1rem; color: var(--slate); line-height: 1.6; }

/* Original pill eyebrow — kept as-is, still used by the CTA section below */
.rk-eyebrow {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-mono); font-size: 0.75rem; font-weight: 500;
  padding: 0.4rem 0.85rem; border-radius: 100px;
}
.rk-eyebrow--dark { color: var(--brass); background: var(--brass-light); }
.rk-eyebrow--light { color: var(--brass-light); background: rgba(232,217,181,0.12); }

/* New kicker — bold left-aligned section marker, used only by Features */
.rk-kicker { display: flex; align-items: center; gap: 0.85rem; }
.rk-kicker__rule { width: 32px; height: 3px; background: var(--brass); border-radius: 2px; flex-shrink: 0; }
.rk-kicker__text {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-mono); font-size: 0.95rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--brass);
}
.rk-kicker__text i { font-size: 1rem; }

/* Neumorphic bento grid */
.rk-bento {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: auto auto;
  gap: 1.5rem;
}
.rk-card--anchor { grid-column: span 2; grid-row: span 2; background: var(--ink); box-shadow: var(--neu-raised-dark); }
.rk-card:not(.rk-card--anchor) { grid-column: span 2; }
.rk-bento .rk-card:nth-child(2) { grid-column: 3 / span 2; }
.rk-bento .rk-card:nth-child(3) { grid-column: 3 / span 1; }
.rk-bento .rk-card:nth-child(4) { grid-column: 4 / span 1; }
.rk-bento .rk-card:nth-child(5) { grid-column: 3 / span 2; }

.rk-card {
  position: relative;
  overflow: hidden; /* clips the watermark icon to the card's rounded corners */
  background: var(--neu-surface);
  box-shadow: var(--neu-raised);
  border-radius: var(--radius-lg);
  padding: 2rem;
  transition: box-shadow 0.3s var(--ease);
}
.rk-card:not(.rk-card--anchor):hover { box-shadow: var(--neu-raised), 0 0 0 1px rgba(176,141,63,0.25); }

.rk-card__body { position: relative; z-index: 1; }

/* Faint per-card icon watermark — echoes the card's own content instead of a stock photo */
.rk-card__watermark {
  position: absolute;
  bottom: -1.2rem;
  right: -1rem;
  font-size: 9rem;
  line-height: 1;
  color: var(--ink);
  opacity: 0.055;
  pointer-events: none;
  z-index: 0;
  transform: rotate(-8deg);
}
.rk-card--anchor .rk-card__watermark {
  color: var(--paper);
  opacity: 0.08;
  font-size: 12rem;
}

.rk-card__icon {
  width: 48px; height: 48px; border-radius: var(--radius-sm);
  background: var(--neu-surface); box-shadow: var(--neu-inset); color: var(--brass);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem; margin-bottom: 1.25rem;
}
.rk-card--anchor .rk-card__icon { background: rgba(176,141,63,0.14); box-shadow: none; color: var(--brass-light); }

.rk-card h3 { font-family: var(--font-body); font-weight: 700; font-size: 1.2rem; color: var(--ink); margin: 0 0 0.75rem; }
.rk-card--anchor h3 { color: var(--paper); font-size: 1.4rem; } /* fixed: was var(--neu-raised-dark), an invalid box-shadow value used as a color */

.rk-card p { font-family: var(--font-body); font-size: 0.95rem; line-height: 1.65; color: var(--slate); margin: 0; }
.rk-card--anchor p { color: rgba(247,246,242,0.75); } /* fixed: was var(--neu-raised), same bug */

.rk-card__tag {
  display: inline-flex; align-items: center; gap: 0.4rem;
  margin-top: 1.5rem; font-family: var(--font-mono); font-size: 0.75rem;
  color: #7FBFA8; background: rgba(79,122,107,0.18);
  padding: 0.4rem 0.75rem; border-radius: 100px;
}

/* CTA — untouched */
.rk-cta { padding: 6.5rem 0; background: var(--ink); text-align: center; }
.rk-cta__inner { max-width: 640px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; }
.rk-cta h2 {
  font-family: var(--font-display); font-weight: 400; font-size: 2.5rem;
  color: var(--paper); margin: 1.25rem 0 1rem; line-height: 1.15;
}
.rk-cta p { font-family: var(--font-body); font-size: 1.1rem; color: rgba(247,246,242,0.7); line-height: 1.6; margin-bottom: 2.25rem; }

.rk-btn { display: inline-flex; align-items: center; gap: 0.65rem; font-family: var(--font-body); font-weight: 600; border-radius: var(--radius-sm); border: none; cursor: pointer; text-decoration: none; transition: all 0.25s var(--ease); }
.rk-btn--primary { background: var(--brass); color: var(--ink); }
.rk-btn--primary:hover { background: var(--brass-light); transform: translateY(-2px); }
.rk-btn--lg { padding: 1.1rem 2.25rem; font-size: 1.05rem; }

@media (max-width: 1024px) {
  .rk-bento { grid-template-columns: repeat(2, 1fr); }
  .rk-card--anchor { grid-column: span 2; grid-row: span 1; }
  .rk-bento .rk-card:nth-child(2),
  .rk-bento .rk-card:nth-child(3),
  .rk-bento .rk-card:nth-child(4),
  .rk-bento .rk-card:nth-child(5) { grid-column: span 1; }
}

@media (max-width: 640px) {
  .rk-section { padding: 4.5rem 0; }
  .rk-section__head h2, .rk-cta h2 { font-size: 2rem; }
  .rk-bento { grid-template-columns: 1fr; }
  .rk-card--anchor { grid-column: span 1; }
  .rk-bento .rk-card:nth-child(2),
  .rk-bento .rk-card:nth-child(3),
  .rk-bento .rk-card:nth-child(4),
  .rk-bento .rk-card:nth-child(5) { grid-column: span 1; }
  .rk-card__watermark { font-size: 6rem; }
  .rk-card--anchor .rk-card__watermark { font-size: 8rem; }
}
</style>